import { createSelector, select } from "@ngrx/store";
import { FetchStatusSelector } from "@obg/ngrx-utils";
import { isNil } from "lodash-es";
import { combineLatest, Observable } from "rxjs";
import { filter, map } from "rxjs/operators";

import { AuthenticationStatus } from "../auth/auth.model";
import { AuthSelector } from "../auth/auth.selector";
import { LoginRequest } from "../auth/session.model";
import { Jurisdiction } from "../jurisdiction/jurisdiction.model";
import { CoreState } from "../state";

import { ACTION_PREFIX } from "./customer-management.action";
import {
	AllowedAction,
	BankDetailsVerificationStatus,
	Customer,
	CustomerManagementState,
	EmailAvailability,
	KycState,
	RequiredAction,
} from "./customer-management.model";

export namespace CustomerManagementSelector {
	export const getState = (state: CoreState): CustomerManagementState => state.customer;

	export const getCustomer = (state: CoreState): Customer | undefined => state.customer.customer;

	export const getCustomerId = (state: CoreState): string | undefined => state.customer.customer?.customerId;

	export const getExpiredSessionUsername = (state: CoreState): string | undefined =>
		state.customer.expiredSessionUsername;

	export const getEmailAvailability = (state: CoreState): EmailAvailability | undefined =>
		state.customer.emailAvailability;

	export const getAllowedAction = (state: CoreState): AllowedAction | undefined =>
		state.customer.customer?.allowedActions;

	export const getRequiredActions = (state: CoreState): RequiredAction[] | undefined =>
		state.customer.customer?.requiredActions;

	export const getCredentials = (state: CoreState): LoginRequest | undefined => state.customer.credentials;

	export const getCountryOfRegistration = (state: CoreState): string | undefined =>
		state.customer.customer?.address.country;

	export const isPasswordMissing = createSelector(
		getCustomer,
		(customer): boolean => !customer?.credentialTypes.find(({ type }) => type === "UsernamePasswordCredential")
	);

	export const hasUnsubscribedFromNewsletter = (state: CoreState): boolean | undefined =>
		state.customer.hasUnsubscribedFromNewsletter;

	export const hasAcceptedLatestTermsAndConditions = (state: CoreState): boolean | undefined =>
		state.customer.hasAcceptedLatestTermsAndConditions;

	export const hasDeclinedLatestTermsAndConditions = (state: CoreState): boolean | undefined =>
		state.customer.hasDeclinedLatestTermsAndConditions;

	export const hasDismissedLatestTermsAndConditions = (state: CoreState): boolean | undefined =>
		state.customer.hasDismissedLatestTermsAndConditions;

	export const hasAcceptedLatestPrivacyPolicy = (state: CoreState): boolean | undefined =>
		state.customer.hasAcceptedLatestPrivacyPolicy;

	export const hasAcceptedEntityLegalNotice = (state: CoreState): boolean | undefined =>
		state.customer.hasAcceptedEntityLegalNotice;

	export const isProfileUpdateSuccess = (state: CoreState): boolean | undefined =>
		state.customer.isProfileUpdateSuccess;

	export const getKYCStatus = (state: CoreState): KycState | undefined => state.customer.customer?.kyc.state;

	export const getTermsAndConditionsJurisdiction = (state: CoreState): Jurisdiction | undefined =>
		state.customer.termsAndConditionsJurisdiction;

	export const hasAcceptedDepositTermsAndConditions = createSelector(getCustomer, (customer): boolean | undefined => {
		if (!customer) {
			return undefined;
		}

		return !customer.legal || isNil(customer.legal.depositTermsAndConditions)
			? true
			: customer.legal.depositTermsAndConditions;
	});

	export const isDepositTermsAndConditionsActive = (state: CoreState): boolean =>
		state.customer.isDepositTermsAndConditionsActive;

	export const isVerificationRequired = createSelector(getCustomer, (customer): boolean => {
		if (!customer) {
			return false;
		}

		return (
			!customer.subscriptions.isPhoneNumberVerified ||
			customer.profile.bankDetailsVerificationStatus !== BankDetailsVerificationStatus.Verified
		);
	});

	export const isBusy = (state: CoreState): boolean => FetchStatusSelector.isBusy(ACTION_PREFIX)(state);

	export const getPersistentData =
		<T>(key: string) =>
		(state: CoreState): T | undefined =>
			state.customer.persistentData?.[key] as T | undefined;

	export const hasAcceptedFundsProtection$ = (state$: Observable<CoreState>): Observable<boolean> =>
		combineLatest([
			state$.pipe(select(AuthSelector.getAuthenticationStatus)),
			state$.pipe(select(hasAcceptedDepositTermsAndConditions)),
		]).pipe(
			filter(([status, hasAccepted]) => status !== AuthenticationStatus.Pending && !isNil(hasAccepted)),
			map(([status, hasAccepted]) => {
				if (status === AuthenticationStatus.Authenticated && hasAccepted) {
					return true;
				}
				return false;
			})
		);

	export const getCustomerPhoneNumberWithExtension = createSelector(getCustomer, (customer): string | undefined =>
		customer?.subscriptions?.phoneExtension && customer?.subscriptions?.phoneNumber
			? `+${customer.subscriptions.phoneExtension} ${customer.subscriptions.phoneNumber}`
			: undefined
	);

	export const getCustomerEmailAddress = createSelector(
		getCustomer,
		(customer): string | undefined => customer?.subscriptions?.email
	);

	export const shouldProfileValidationOccur = createSelector(
		getCustomer,
		(customer): boolean | undefined => customer?.profile?.shouldProfileValidationOccur
	);
}
